import { Box } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useGetWalletQuery } from './services/services';
import { RootState } from '../store/store';
import { useDispatch, useSelector } from 'react-redux';
import Balances from './balances';
import { setGetWallet } from '../store/slices/walletsSlice';
import { useCompanyBalance } from '../hooks/useCompanyBalance';

interface BalanceProps {
    /**
     * Increase this number to force refresh of `GetBalance` query.
     * (Used by pages that need an automatic balance update after a transaction.)
     */
    autoRefreshKey?: number;
}

const Balance = ({ autoRefreshKey }: BalanceProps) => {
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
    const { data: wallets, isLoading: isWalletsLoading } = useGetWalletQuery(currentUserState?.companyId, { skip: !currentUserState?.companyId });
    const {
        data: getBalance,
        isLoading: isBalanceLoading,
        refetch: refetchBalance,
        isFetching,
        isUninitialized,
        canReadCompanyBalance
    } = useCompanyBalance(currentUserState?.companyId);
    const dispatch = useDispatch();

    interface Balance {
        currencyId: number;
        balance: number;
    }

    useEffect(() => {
        if (wallets && wallets.length > 0) {
            dispatch(setGetWallet(wallets));
        }
    }, [wallets])

    const getWallet = (balances: Balance[]) => {
        return wallets?.map((x) => ({
            ...x,
            balance: balances.find((balance: Balance) => balance.currencyId == x.currencyId)?.balance ?? x.balance
        }));
    }

    // Computed balances using useMemo
    const updatedWallets = useMemo(() => {
        if (getBalance?.data?.wallets && getBalance.data.wallets.length > 0 && wallets) {
            const balances = getBalance.data.wallets.map(wallet => ({
                currencyId: wallet.balanceDetails[0].balanceType,
                balance: wallet.balanceDetails[0].balance
            }));
            return getWallet(balances);
        }
        return wallets;
    }, [getBalance, wallets, isFetching]);


    const getBalanceRefresh = () => {
        if (!isUninitialized) {
            refetchBalance();
        }
    }

    useEffect(() => {
        // Avoid triggering on initial render when value is 0/undefined
        if (autoRefreshKey && autoRefreshKey > 0 && !isUninitialized) {
            refetchBalance();
        }
    }, [autoRefreshKey, refetchBalance, isUninitialized]);


    return (
        <>
            {canReadCompanyBalance ? (
                <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: {
                        xs: 1.5,
                        sm: 2,
                        md: 3
                    }
                }}>
                    <Balances
                        wallets={updatedWallets}
                        userInfo={currentUserState}
                        onRefreshBalance={getBalanceRefresh}
                        isLoading={isWalletsLoading || isBalanceLoading}
                    />
                </Box>
            ) : (
                <></>
            )}
        </>
    )
}

export default Balance